import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  BlockGrid,
  Container,
  ListItem,
  Row,
  RowColumn,
  Section,
  SectionTitle,
} from 'chaoskit/src/components'
import { StylesContainerVariables } from 'chaoskit/src/components/Container'
import { text } from 'chaoskit/src/assets/styles/utility'
import { MDXRenderer } from 'gatsby-plugin-mdx'
import { useRef } from 'react'

import BaseLayout from '~layouts/BaseLayout'
import Data from '~components/Data'
import Hero, { HeroContent, HeroTitle, HeroTitleSub } from '~partials/Hero'
import HelmetSEO from '~partials/global/HelmetSEO'
import {
  HomeHeroGrid,
  HomeHeroGridContent,
  HomeHeroGridImage,
} from '~partials/home/Hero'
import ButtonLink from '~components/ButtonLink'
import heroImage from '~media/partners/hero.svg'
import goToMarket from '~media/partners/go-to-market.svg'
import partnerPortal from '~media/partners/partner-portal.svg'
import salesEnablement from '~media/partners/sales-enablement.svg'
import certification from '~media/partners/certification.svg'
import ecosystem from '~media/partners/track-ecosystem.svg'
import managedService from '~media/partners/track-managed-service.svg'
import referral from '~media/partners/track-referral.svg'
import reseller from '~media/partners/track-reseller.svg'
import systemIntegrators from '~media/partners/track-system-integrators.svg'
import Image from '~components/Image'
import MarketoForm from '~components/MarketoForm'
import { getPaddedStickyMenuOffset, stickyIt } from '~styles/utility'

function OverviewAsset({ id }) {
  let src

  // eslint-disable-next-line default-case
  switch (id) {
    case 'go-to-market':
      src = goToMarket
      break
    case 'partner-portal':
      src = partnerPortal
      break
    case 'sales-enablement':
      src = salesEnablement
      break
    case 'certification':
      src = certification
      break
  }

  if (!src) {
    return null
  }

  return <Image src={src} alt="" css={{ height: 150 }} />
}

OverviewAsset.propTypes = {
  id: PropTypes.string,
}

function TrackAsset({ id }) {
  let src

  // eslint-disable-next-line default-case
  switch (id) {
    case 'ecosystem':
      src = ecosystem
      break
    case 'managed-service':
      src = managedService
      break
    case 'referral':
      src = referral
      break
    case 'reseller':
      src = reseller
      break
    case 'system-integrators':
      src = systemIntegrators
      break
  }

  if (!src) {
    return null
  }

  return <Image src={src} alt="" css={{ width: 180 }} />
}

TrackAsset.propTypes = {
  id: PropTypes.string,
}

// Utility that handles smooth-scrolling
const scrollToRef = ({ theme, ref }) => {
  window.scrollTo({
    top:
      ref.current.getBoundingClientRect().top +
      window.scrollY -
      getPaddedStickyMenuOffset(theme),
    behavior: 'smooth',
  })
}

const Partners = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()
  const formContainerRef = useRef()

  return (
    <BaseLayout
      headerProps={{ contrast: false }}
      footerProps={{ hideCTA: true }}
    >
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        image={pageData.socialMediaImage}
      />
      <Hero
        contrast={false}
        css={{
          background: theme.brand.stone100,
          position: 'relative',
          paddingBottom: 100, // @NOTE Is the same as our angular cut-out graphic
        }}
      >
        <HeroContent>
          <HomeHeroGrid
            css={{
              maxWidth: StylesContainerVariables.large,
            }}
          >
            <HomeHeroGridImage
              css={{
                [theme.mq.medium]: {
                  gridColumn: '6 / span 7',
                  img: {
                    height: 370,
                  },
                },
              }}
            >
              <Image src={heroImage} />
            </HomeHeroGridImage>
            <HomeHeroGridContent
              css={{
                [theme.mq.medium]: {
                  gridRow: 1,
                  gridColumn: '1 / span 5',
                },
              }}
            >
              <HeroTitle size="large">
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <HeroTitleSub>
                <Data>{pageData.hero.blurb}</Data>
              </HeroTitleSub>
              <div css={{ marginTop: theme.space.large }}>
                <ButtonLink
                  type="primary"
                  onClick={() => {
                    scrollToRef({ theme, ref: formContainerRef })
                  }}
                >
                  Apply now
                </ButtonLink>
              </div>
            </HomeHeroGridContent>
          </HomeHeroGrid>
        </HeroContent>
      </Hero>
      <Section>
        <Container>
          <SectionTitle
            as="h2"
            title={<Data>{pageData.partnershipOverview.heading}</Data>}
            sub={<Data>{pageData.partnershipOverview.blurb}</Data>}
            css={{
              '.CK__SectionTitle__Sub': {
                maxWidth: '100%',
              },
            }}
          />
          <BlockGrid size={{ medium: 2 }} gutter={{ base: 'large' }}>
            {pageData.partnershipOverview.items.map((item) => (
              <ListItem key={item.heading} css={{ textAlign: 'center' }}>
                <OverviewAsset id={item.imageId} />
                <h6>
                  <Data>{item.heading}</Data>
                </h6>
                <p>
                  <Data>{item.blurb}</Data>
                </p>
              </ListItem>
            ))}
          </BlockGrid>
        </Container>
      </Section>
      <Section>
        <Container>
          <Row gutter={{ base: 'large' }}>
            <RowColumn size={{ medium: 5 }}>
              <SectionTitle
                as="h2"
                space="large"
                align={{ medium: 'left' }}
                title={<Data>{pageData.tracksTiers.heading}</Data>}
                sub={<Data>{pageData.tracksTiers.blurb}</Data>}
              />
              <div
                css={{
                  [theme.mq.medium]: [stickyIt(theme)],
                }}
              >
                <div id="apply-now" ref={formContainerRef}>
                  <h3>{pageData.tracksTiers.form.heading}</h3>
                  <MarketoForm
                    id="1419"
                    displayOptions={{
                      submitButtonCentered: false,
                    }}
                  />
                </div>
              </div>
            </RowColumn>
            <RowColumn size={{ medium: 7 }}>
              <BlockGrid gutter={{ base: 'large' }}>
                {pageData.tracksTiers.items.map((item) => (
                  <ListItem key={item.heading}>
                    <div
                      css={{
                        display: 'grid',
                        gap: theme.space.base,
                        justifyContent: 'start',

                        [theme.mq.medium]: {
                          gridAutoFlow: 'column',
                          gridAutoColumns: 'auto',
                        },
                      }}
                    >
                      <TrackAsset id={item.imageId} />
                      <div>
                        <div
                          css={{
                            ...theme.text.base,
                            ...text.expanded(theme),
                            fontWeight: theme.fontWeight.bold,
                            marginBottom: theme.space.small,
                          }}
                        >
                          <Data>{item.heading}</Data>
                        </div>
                        <div>
                          <Data>{item.blurb}</Data>
                        </div>
                      </div>
                    </div>
                  </ListItem>
                ))}
              </BlockGrid>
            </RowColumn>
          </Row>
        </Container>
      </Section>
      <Section
        className="u-contrast"
        css={{
          background: theme.brand.darkViolet,
          position: 'relative',
          paddingBottom: theme.space.large,
        }}
      >
        <Container>
          <SectionTitle
            as="h2"
            title={<Data>{pageData.partneringWithTheBest.heading}</Data>}
            sub={
              <MDXRenderer>
                {pageData.partneringWithTheBest.content}
              </MDXRenderer>
            }
            css={{
              '.CK__SectionTitle__Sub a': {
                textDecoration: 'underline',
              },
            }}
          />
        </Container>
      </Section>
    </BaseLayout>
  )
}

Partners.propTypes = {
  data: PropTypes.object.isRequired,
}

export default Partners

export const pageQuery = graphql`
  query PartnersPageData {
    file(name: { eq: "partners" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        body
        frontmatter {
          title
          description
          socialMediaImage
          hero {
            heading
            blurb
          }
          partnershipOverview {
            heading
            blurb
            items {
              imageId
              heading
              blurb
            }
          }
          tracksTiers {
            heading
            blurb
            form {
              heading
            }
            items {
              imageId
              heading
              blurb
            }
          }
          partneringWithTheBest {
            heading
            content
          }
        }
      }
    }
  }
`
